import { useEffect } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { useAskYogiService } from 'features/ask-yogi/service'
import type {
  AgentCitationPost,
  AgentPostTextTheme,
} from 'features/ask-yogi/types'

/**
 * Batch-fetch every citation preview (and any named sentence's
 * theme/sentiment) for a message in a single request, then seed the
 * per-citation and per-sentence react-query caches so each `<Citation>` /
 * `<VerbatimCard>` reads its data from cache (see `useCitationData` /
 * `usePostTextTheme`) without firing its own request. This is what
 * collapses the former per-citation N+1 into one call per message.
 *
 * The per-id cache keys (`['citation', projectId, id]` /
 * `['citation-theme', projectId, id]`) are shared with those hooks and
 * stable across messages, so a post/sentence cited in two messages is
 * fetched once. Ids the backend omits (not found / outside the project, or
 * no visible theme) are seeded `null` so their consumer shows its "not
 * found" fallback rather than spinning forever.
 */
export function useCitationsData(
  projectId: string,
  citationIds: string[],
  postTextIds: string[] = [],
): void {
  const askYogiService = useAskYogiService()
  const queryClient = useQueryClient()

  const numericIds = citationIds
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
  const numericPostTextIds = postTextIds
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0)
  // Stable, order-independent keys so the same citation/post_text set dedupes.
  const idKey = [...numericIds].sort((a, b) => a - b).join(',')
  const postTextIdKey = [...numericPostTextIds].sort((a, b) => a - b).join(',')

  const { data } = useQuery(
    ['citations', projectId, idKey, postTextIdKey],
    () =>
      askYogiService.getCitationPostsWithThemes(
        projectId,
        numericIds,
        numericPostTextIds,
      ),
    {
      enabled: !!projectId && numericIds.length > 0,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  )

  useEffect(() => {
    if (!data) return
    const postsById = new Map<number, AgentCitationPost>(
      data.posts.map((post) => [post.post_id, post]),
    )
    // Derive the requested ids from the stable keys so the effect's deps
    // stay honest (no need to depend on the per-render array identities).
    const requestedIds = idKey ? idKey.split(',').map(Number) : []
    for (const id of requestedIds) {
      queryClient.setQueryData(
        ['citation', projectId, String(id)],
        postsById.get(id) ?? null,
      )
    }

    const themesByPostTextId = new Map<number, AgentPostTextTheme>(
      data.post_text_themes.map((theme) => [theme.post_text_id, theme]),
    )
    const requestedPostTextIds = postTextIdKey
      ? postTextIdKey.split(',').map(Number)
      : []
    for (const postTextId of requestedPostTextIds) {
      queryClient.setQueryData(
        ['citation-theme', projectId, String(postTextId)],
        themesByPostTextId.get(postTextId) ?? null,
      )
    }
  }, [data, idKey, postTextIdKey, projectId, queryClient])
}
